import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Shield, Award, CheckCircle2, ChevronRight, Star, Camera,
  FileCheck, Clock, BadgeCheck, ArrowRight
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CertificationPage = () => {
  const [tiers, setTiers] = useState([]);
  const [selectedTier, setSelectedTier] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [formData, setFormData] = useState({
    device_type: '',
    device_model: '',
    serial_number: '',
    seller_name: '',
    seller_contact: '',
    inspection_type: '',
    contact_email: '',
    contact_phone: ''
  });

  useEffect(() => {
    fetchTiers();
  }, []);

  const fetchTiers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/certification/tiers`);
      if (res.ok) {
        const data = await res.json();
        setTiers(data.tiers || []);
      }
    } catch (error) {
      console.error('Error fetching tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectTier = (tier) => {
    setSelectedTier(tier);
    setFormData({ ...formData, inspection_type: tier.id });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const res = await fetch(`${API_URL}/api/certification/request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Solicitud de certificación enviada');
        setShowForm(false);
      } else {
        toast.error('Error al enviar solicitud');
      }
    } catch (error) {
      toast.error('Error de conexión');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="certification-page">
      {/* Hero */}
      <div className="bg-gradient-to-br from-emerald-600 to-teal-700 text-white">
        <div className="max-w-7xl mx-auto px-4 py-16">
          <div className="flex items-center gap-2 text-sm text-emerald-200 mb-4">
            <Link to="/" className="hover:text-white">Inicio</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">Certificación Segunda Mano</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4 flex items-center gap-4">
              <BadgeCheck className="w-12 h-12" />
              Certificación Segunda Mano
            </h1>
            <p className="text-xl text-emerald-100 mb-6">
              Compra o vende con confianza. Inspección profesional y sello de garantía 
              para consolas y dispositivos usados.
            </p>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <Shield className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Hasta 90 días garantía</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <FileCheck className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Certificado oficial</p>
              </div>
              <div className="bg-white/10 rounded-xl p-4 text-center">
                <Camera className="w-8 h-8 mx-auto mb-2" />
                <p className="text-sm">Informe con fotos</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Tiers */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        <h2 className="text-2xl font-bold mb-8 text-center">Niveles de Certificación</h2>
        
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6">
            {tiers.map((tier, i) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                className={`bg-white rounded-2xl border-2 p-6 hover:shadow-xl transition-all ${
                  tier.id === 'premium' ? 'border-emerald-500 relative' : 'border-gray-200'
                }`}
                data-testid={`cert-tier-${tier.id}`}
              >
                {tier.id === 'premium' && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    RECOMENDADO
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                  <p className="text-4xl font-bold text-emerald-600">{tier.price}</p>
                  <p className="text-sm text-gray-500">Tiempo: {tier.time}</p>
                </div>
                
                <ul className="space-y-3 mb-6">
                  {tier.includes.map((item, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                
                <div className="p-3 bg-emerald-50 rounded-lg mb-4 text-center">
                  <p className="text-sm font-medium text-emerald-700">
                    Garantía: {tier.warranty}
                  </p>
                </div>
                
                <Button 
                  onClick={() => selectTier(tier)}
                  className={`w-full ${tier.id === 'premium' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-gray-800 hover:bg-gray-900'}`}
                >
                  Solicitar Certificación
                </Button>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* How it works */}
      <div className="bg-gray-100 py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold mb-8 text-center">Cómo Funciona</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { step: 1, title: 'Solicita', desc: 'Elige tu nivel de certificación y envía el formulario' },
              { step: 2, title: 'Envía', desc: 'Recibe etiqueta de envío gratuita para tu dispositivo' },
              { step: 3, title: 'Inspeccionamos', desc: 'Nuestros técnicos realizan la inspección completa' },
              { step: 4, title: 'Certificamos', desc: 'Recibes certificado + sello + garantía' }
            ].map((item) => (
              <div key={item.step} className="text-center">
                <div className="w-12 h-12 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto"
          >
            <h2 className="text-xl font-bold mb-2">Solicitar Certificación</h2>
            {selectedTier && (
              <p className="text-emerald-600 text-sm mb-4">{selectedTier.name} - {selectedTier.price}</p>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">Tipo de dispositivo *</label>
                <select
                  required
                  value={formData.device_type}
                  onChange={(e) => setFormData({...formData, device_type: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg p-2"
                >
                  <option value="">Seleccionar...</option>
                  <option value="console">Consola</option>
                  <option value="controller">Mando</option>
                  <option value="handheld">Portátil</option>
                  <option value="mobile">Móvil</option>
                </select>
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Modelo *</label>
                <Input
                  required
                  placeholder="PS5, Nintendo Switch..."
                  value={formData.device_model}
                  onChange={(e) => setFormData({...formData, device_model: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Nº Serie (si lo conoces)</label>
                <Input
                  placeholder="ABC123..."
                  value={formData.serial_number}
                  onChange={(e) => setFormData({...formData, serial_number: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Nombre del vendedor *</label>
                <Input
                  required
                  placeholder="Nombre de quien vende"
                  value={formData.seller_name}
                  onChange={(e) => setFormData({...formData, seller_name: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Contacto del vendedor</label>
                <Input
                  placeholder="Email o teléfono"
                  value={formData.seller_contact}
                  onChange={(e) => setFormData({...formData, seller_contact: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Tu email *</label>
                <Input
                  type="email"
                  required
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                />
              </div>
              
              <div>
                <label className="text-sm text-gray-600">Tu teléfono *</label>
                <Input
                  type="tel"
                  required
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                  Enviar Solicitud
                </Button>
                <Button 
                  type="button" 
                  variant="outline"
                  onClick={() => setShowForm(false)}
                >
                  Cancelar
                </Button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default CertificationPage;
